import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsdotWeatherInformationApiMeta } from "../apiMeta";
import {
  type WeatherInformationByStationIdInput,
  weatherInformationByStationIdInputSchema,
} from "./shared/weatherInfo.input";
import {
  type WeatherInfo,
  weatherInfoSchema,
} from "./shared/weatherInfo.output";

/**
 * Metadata for the fetchWeatherInformationByStationId endpoint
 */
export const weatherInformationByStationIdMeta = {
  functionName: "fetchWeatherInformationByStationId",
  endpoint:
    "/GetCurrentWeatherInformationByStationIDAsJson?StationID={StationID}",
  inputSchema: weatherInformationByStationIdInputSchema,
  outputSchema: weatherInfoSchema,
  sampleParams: { StationID: 1909 },
  endpointDescription:
    "Get current weather information for a specific station by ID.",
} satisfies EndpointMeta<WeatherInformationByStationIdInput, WeatherInfo>;

/**
 * Fetch function for retrieving current weather information for a specific station by ID
 */
const { fetch, useHook } = createFetchAndHook<
  WeatherInformationByStationIdInput,
  WeatherInfo
>({
  api: wsdotWeatherInformationApiMeta,
  endpoint: weatherInformationByStationIdMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/weatherInfo.endpoints").weatherInfoGroup.cacheStrategy,
});

/**
 * Fetch function for retrieving current weather information for a specific station by ID
 */
export const fetchWeatherInformationByStationId: FetchFactory<
  WeatherInformationByStationIdInput,
  WeatherInfo
> = fetch;

/**
 * React Query hook for retrieving current weather information for a specific station by ID
 */
export const useWeatherInformationByStationId: HookFactory<
  WeatherInformationByStationIdInput,
  WeatherInfo
> = useHook;
